import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from 'components/common/buttons/IconButton';
import { useReactiveVar } from '@apollo/client';
import { detailsLoadedItemIdsVar, isAssetLevelResidualValuesVar, isGridEditableVar } from '../../variables';
import WrappedFakeText from 'components/common/WrappedFakeText';
import ResidualValueGridCombinedItemDetailsComponent from './ResidualValueGridCombinedItemDetailsComponent';
import ResidualValueGridSplitItemDetailsComponent from './ResidualValueGridSplitItemDetailsComponent';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_RESIDUAL_VALUE_GRIDS_WRITE } from 'constants/rightsCodes';
import { items, required, shape, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useMutation } from 'hooks/apollo/useMutation';
import { SAVE_RESIDUAL_VALUE_GRID_ITEMS_DETAILS } from 'features/residualValueGrid/mutations';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import useSubscription from 'hooks/apollo/useSubscription';
import { RESIDUAL_VALUE_GRID_ITEMS_DETAILS_SAVED } from 'features/residualValueGrid/subscriptions';
import { ItemsDetailsCard } from '../../Styles';
import { Prompt } from 'react-router-dom';
import { isPositiveOrZero } from 'utils/simple-validations';

const validator = shape({
  details: items(
    shape({
      mileageFrom: [required, isPositiveOrZero] |> stopOnFirstFailure,
      mileageTo: [required, isPositiveOrZero] |> stopOnFirstFailure,
      period: [required, isPositiveOrZero] |> stopOnFirstFailure,
      rvPercent: [required, isPositiveOrZero] |> stopOnFirstFailure
    })
  ),
  items: items(
    shape({
      details: items(
        shape({
          mileageFrom: [required, isPositiveOrZero] |> stopOnFirstFailure,
          mileageTo: [required, isPositiveOrZero] |> stopOnFirstFailure,
          period: [required, isPositiveOrZero] |> stopOnFirstFailure,
          rvPercent: [required, isPositiveOrZero] |> stopOnFirstFailure
        })
      )
    })
  )
});

const ResidualValueGridItemDetailsContainer = ({ id }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  // #region State

  const isEditable = useReactiveVar(isGridEditableVar);
  const isAssetLevelResidualValues = useReactiveVar(isAssetLevelResidualValuesVar);
  const detailsLoadedItemIds = useReactiveVar(detailsLoadedItemIdsVar);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens({ details: [], items: [] });

  const [pending, setPending] = useState(false);

  // #endregion

  const canWrite = usePermission(ADMIN_RESIDUAL_VALUE_GRIDS_WRITE);

  const [validation, validate, resetValidation] = useDirtyFieldValidation(validator);

  // #region Derived state

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const loading = useMemo(
    () =>
      isAssetLevelResidualValues === undefined || isAssetLevelResidualValues === null || isEditable === undefined || isEditable === null,
    [isAssetLevelResidualValues, isEditable]
  );

  const disabled = useMemo(() => !canWrite || !isEditable || pending, [canWrite, isEditable, pending]);

  // #endregion

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  // #region Save

  const [saveItemsDetails] = useMutation(SAVE_RESIDUAL_VALUE_GRID_ITEMS_DETAILS, {
    onError: _ => {
      setPending(false);
    }
  });

  const save = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    setPending(true);

    const detailSanitizer = detail => ({ ...detail, id: typeof detail.id === 'string' ? null : detail.id });

    const input = { gridId: id };

    if (isAssetLevelResidualValues) {
      input.items = data.items.map(item => ({ id: item.id, details: item.details.map(detailSanitizer) }));
      input.details = null;
      input.excludeDetailsForItemIds = data.items.map(item => item.id).filter(itemId => !detailsLoadedItemIds.includes(itemId));
    } else {
      input.items = data.items.map(item => ({ id: item.id, details: null }));
      input.details = data.details.map(detailSanitizer);
    }

    saveItemsDetails({ variables: { ...input } });
  };

  useSubscription(RESIDUAL_VALUE_GRID_ITEMS_DETAILS_SAVED, {
    onError: error => addToast(error, 'error'),
    variables: { id },
    onData: _ => {
      resetLens();
      resetValidation();
      setPending(false);
      addToast(t('ResidualValueGrid.ItemsDetailsSaved'), 'success');
    }
  });

  // #endregion

  return (
    <>
      <Prompt when={isEditable && isDirty} message={t('LeavingWithoutSaving')} />
      <ItemsDetailsCard
        icon={MenuIcon}
        actions={<IconButton type='save' onClick={save} disabled={disabled || !isDirty} />}
        isSplit={!isAssetLevelResidualValues}
      >
        {loading && <WrappedFakeText />}
        {!loading && !isAssetLevelResidualValues && (
          <ResidualValueGridSplitItemDetailsComponent
            id={id}
            lens={lens}
            resetLens={resetLens}
            validation={validation}
            disabled={disabled}
          />
        )}
        {!loading && isAssetLevelResidualValues && (
          <ResidualValueGridCombinedItemDetailsComponent
            id={id}
            lens={lens}
            resetLens={resetLens}
            validation={validation}
            disabled={disabled}
            pending={pending}
          />
        )}
      </ItemsDetailsCard>
    </>
  );
};

ResidualValueGridItemDetailsContainer.propTypes = {
  id: PropTypes.number
};

export default ResidualValueGridItemDetailsContainer;
